// login.component.ts  (Student)
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '../shared/common.service';
import { ToastService } from '../shared/toast.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SocketService } from '../services/socket.service';
import { InactivityService } from '../services/inactivity.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnDestroy {

  email    = '';
  password = '';
  role     = 'student';
  rememberMe   = false;
  showPassword = false;
  submitted    = false;


  showPasswordChangeModal = false;
  tempToken = '';
  newPassword = '';
  confirmNewPassword = '';
  passwordChangeError = '';
  passwordChangeLoading = false;
  showNewPw = false;
  showConfirmPw = false;
  // ── OTP state ──────────────────────────────────────────────
  showOtpModal   = false;
  otpDigits      = ['', '', '', '', '', ''];
  otpError       = '';
  otpShake       = false;
  isOtpSending   = false;   // disables the main Login button while requesting code
  isOtpVerifying = false;   // disables the Verify button inside the modal

  resendCooldown  = 60;
  resendRemaining = 0;
  private resendTimer: any = null;
  // private _otpHandledByBeforeInput = false;
  private getOtpInput(index: number): HTMLInputElement | null {
  return document.getElementById(`otp-${index}`) as HTMLInputElement | null;
}

  videoUrl!: SafeResourceUrl;

  constructor(
    private cs:          CommonService,
    private router:      Router,
    private route:       ActivatedRoute,
    private toast:       ToastService,
    private sanitizer:   DomSanitizer,
    private socketService: SocketService,
    private inactivity:  InactivityService
  ) {}

  ngOnInit(): void {
    this.role = 'student';
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube.com/embed/rL4zzMLGBCs'
    );
    this.route.queryParams.subscribe(p => {
      if (p['email']) this.email = p['email'];
    });
  }

  ngOnDestroy(): void {
    if (this.resendTimer) clearInterval(this.resendTimer);
  }

  // ── STEP 1: Submit form → request OTP ──────────────────────
  onLogin(): void {
    this.submitted = true;
    if (!this.email || !this.password) {
      this.toast.showError('Email and password are required.');
      return;
    }

    const payload = { email: this.email, password: this.password, role: 'student' };

    this.isOtpSending = true;

    this.cs.requestLoginOtp(payload).subscribe({
      next: (res: any) => {
        this.isOtpSending = false;
        // Open OTP modal
        this.otpDigits   = ['', '', '', '', '', ''];
        this.otpError    = '';
        this.showOtpModal = true;
        this.startResendCooldown();
        this.toast.showSuccess(res?.message || 'Verification code sent to your email.');
      },
      error: (err: any) => {
        this.isOtpSending = false;
        this.toast.showError(err?.error?.message || 'Login failed. Please try again.');
      }
    });
  }

  // ── STEP 2: Verify OTP → complete login ────────────────────
  verifyOtpAndLogin(): void {
    const otpCode = this.otpDigits.join('').trim();
    if (!/^\d{6}$/.test(otpCode)) {
      this.triggerShake('Please enter the complete 6-digit code.');
      return;
    }

    const payload = {
      email:    this.email,
      password: this.password,
      role:     'student',
      otpCode
    };

    this.isOtpVerifying = true;
    this.otpError       = '';

    this.cs.login(payload).subscribe({
      next: (res: any) => {
        this.isOtpVerifying = false;
        this.showOtpModal   = false;

        this.toast.showSuccess(res?.message || 'Login successful!');

        // Always store basic identifiers
        if (res?.token) {
          localStorage.setItem('authToken', res.token);
          localStorage.setItem('email', res.user?.email || this.email);
        }
        if (res?.user?._id) {
          localStorage.setItem('userId', res.user._id);
          const name = `${res.user?.firstName || ''} ${res.user?.lastName || ''}`.trim();
          localStorage.setItem('name', name);
        }
        if (res?.user?.roleProfile?.packageId) {
          localStorage.setItem('packageId', res.user.roleProfile.packageId);
        }

        // ✅ INTERCEPT — must change password before anything else
        if (res?.mustChangePassword) {
          this.showOtpModal = false;
          this.tempToken = res.token; // keep token for the change-password API call
          this.newPassword = '';
          this.confirmNewPassword = '';
          this.passwordChangeError = '';
          this.showPasswordChangeModal = true;
          return; // stop here — no navigation, no socket init
        }

        // Normal login flow
        this.socketService.join(res.user._id);
        this.socketService.refreshAuthToken();
        this.inactivity.start();

        const role = (res?.user?.role || 'student').toLowerCase();
        localStorage.setItem('role', role);

        const redirectMap: Record<string, string> = {
          admin: '/admin/dashboard',
          instructor: '/instructor/dashboard',
          student: '/student/dashboard'
        };
        setTimeout(() => this.router.navigate([redirectMap[role] || '/']), 200);
      },
      error: (err: any) => {
        this.isOtpVerifying = false;
        const msg = err?.error?.message || 'Invalid code. Please try again.';
        this.triggerShake(msg);
      }
    });
  }

  // ── Resend ─────────────────────────────────────────────────
  resendOtp(): void {
    if (this.resendRemaining > 0 || this.isOtpSending) return;

    const payload = { email: this.email, password: this.password, role: 'student' };
    this.isOtpSending = true;
    this.startResendCooldown();

    this.cs.requestLoginOtp(payload).subscribe({
      next: (res: any) => {
        this.isOtpSending = false;
        this.otpDigits    = ['', '', '', '', '', ''];
        this.otpError     = '';
        this.toast.showSuccess(res?.message || 'New code sent.');
      },
      error: (err: any) => {
        this.isOtpSending = false;
        this.toast.showError(err?.error?.message || 'Failed to resend code.');
      }
    });
  }

  // ── Close modal ────────────────────────────────────────────
  closeOtpModal(): void {
    if (this.isOtpVerifying) return;
    this.showOtpModal = false;
    this.otpError     = '';
    if (this.resendTimer) { clearInterval(this.resendTimer); this.resendTimer = null; }
    this.resendRemaining = 0;
  }

// onOtpBeforeInput(e: InputEvent, index: number): void {
//   const data      = (e as any).data as string | null;
//   const inputType = (e as any).inputType as string | undefined;
 
//   if (inputType === 'insertText' && data && /^\d$/.test(data)) {
//     e.preventDefault();
 
//     this._otpHandledByBeforeInput = true;
 
//     // Write digit into current box
//     this.otpDigits[index] = data;
//     const el = document.getElementById(`otp-${index}`) as HTMLInputElement;
//     if (el) el.value = data;
 
//     if (index < 5) {
//       // ✅ Clear the NEXT box before focusing it — prevents ghost digit
//       this.otpDigits[index + 1] = '';
//       const next = document.getElementById(`otp-${index + 1}`) as HTMLInputElement;
//       if (next) {
//         next.value = '';          // clear DOM value
//         next.focus();             // move focus — NO select()
//       }
//     }
 
//     // Reset flag after the browser's input-event cycle finishes
//     setTimeout(() => { this._otpHandledByBeforeInput = false; }, 0);
//   }
// }
 
// onOtpInput(event: Event, index: number): void {
//   // Skip if beforeinput already handled this keystroke
//   if (this._otpHandledByBeforeInput) return;
 
//   const input = event.target as HTMLInputElement;
//   const digit = (input.value || '').replace(/\D/g, '').slice(-1);
 
//   this.otpDigits[index] = digit;
//   input.value = digit;
 
//   if (digit && index < 5) {
//     // ✅ Clear next box before focusing (same fix for mobile / non-beforeinput path)
//     this.otpDigits[index + 1] = '';
//     const next = document.getElementById(`otp-${index + 1}`) as HTMLInputElement;
//     if (next) {
//       next.value = '';
//       next.focus();               // NO select()
//     }
//   }
// }

  onOtpKeyDown(e: KeyboardEvent, index: number): void {
  const input = e.target as HTMLInputElement;
 
  // Digit key
  if (/^\d$/.test(e.key)) {
    e.preventDefault();
    input.value = e.key;                    // write to DOM
    this.otpDigits[index] = e.key;          // sync model
 
    if (index < 5) {
      this.getOtpInput(index + 1)?.focus(); // move to next — synchronous, no setTimeout
    }
    return;
  }
 
  // Backspace
  if (e.key === 'Backspace') {
    e.preventDefault();
    if (input.value) {
      input.value = '';
      this.otpDigits[index] = '';
    } else if (index > 0) {
      this.otpDigits[index - 1] = '';
      const prev = this.getOtpInput(index - 1);
      if (prev) { prev.value = ''; prev.focus(); }
    }
    return;
  }
 
  // Arrow keys
  if (e.key === 'ArrowLeft')  { e.preventDefault(); this.getOtpInput(index - 1)?.focus(); return; }
  if (e.key === 'ArrowRight') { e.preventDefault(); this.getOtpInput(index + 1)?.focus(); return; }
 
  // Block non-digit printable characters
  if (e.key.length === 1) e.preventDefault();
}
 
// ── REPLACE onOtpPaste ────────────────────────────────────────────────
onOtpPaste(e: ClipboardEvent): void {
  e.preventDefault();
  const text = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
  if (!text) return;
 
  this.otpDigits = ['', '', '', '', '', ''];
  text.split('').forEach((ch, i) => {
    this.otpDigits[i] = ch;
    const el = this.getOtpInput(i);
    if (el) el.value = ch;
  });
 
  this.getOtpInput(Math.min(text.length - 1, 5))?.focus();
}

  // ── Helpers ────────────────────────────────────────────────
  private triggerShake(msg: string): void {
    this.otpError = msg;
    this.otpShake = true;
    setTimeout(() => (this.otpShake = false), 450);
  }

  private startResendCooldown(): void {
    this.resendRemaining = this.resendCooldown;
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.resendTimer = setInterval(() => {
      this.resendRemaining--;
      if (this.resendRemaining <= 0) {
        clearInterval(this.resendTimer);
        this.resendTimer     = null;
        this.resendRemaining = 0;
      }
    }, 1000);
  }

  togglePasswordVisibility(field: string): void {
    if (field === 'password') this.showPassword = !this.showPassword;
  }

  goToStaffLogin(role: 'admin' | 'instructor'): void {
    this.router.navigate(['/admin-instructor-login'], { queryParams: { role } });
  }


  // ── First-time password change ─────────────────────────────
  submitPasswordChange(): void {
    this.passwordChangeError = '';

    if (!this.newPassword || !this.confirmNewPassword) {
      this.passwordChangeError = 'Both password fields are required.';
      return;
    }
    if (this.newPassword !== this.confirmNewPassword) {
      this.passwordChangeError = 'Passwords do not match.';
      return;
    }
    if (this.newPassword.length < 8) {
      this.passwordChangeError = 'Password must be at least 8 characters.';
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(this.newPassword)) {
      this.passwordChangeError = 'Must include uppercase, lowercase, number, and special character (@$!%*?&).';
      return;
    }

    this.passwordChangeLoading = true;

    this.cs.firstTimeChangePassword(
      { newPassword: this.newPassword, confirmPassword: this.confirmNewPassword },
      this.tempToken
    ).subscribe({
      next: (res: any) => {
        this.passwordChangeLoading = false;
        if (res?.status === 1) {
          // ✅ Clear everything — force a fresh login
          localStorage.clear();
          this.tempToken = '';
          this.showPasswordChangeModal = false;
          this.email = '';
          this.password = '';
          this.newPassword = '';
          this.confirmNewPassword = '';

          this.toast.showSuccess('Password set! Please log in with your new password.');

          // Navigate back to login — fresh start
          setTimeout(() => this.router.navigate(['/login']), 1800);
        } else {
          this.passwordChangeError = res?.message || 'Failed to change password.';
        }
      },
      error: (err: any) => {
        this.passwordChangeLoading = false;
        this.passwordChangeError = err?.error?.message || 'Failed to change password. Please try again.';
      }
    });
  }

  closePasswordChangeModal(): void {
    // Intentionally blocked — user MUST set password
    this.toast.showError('You must set a new password to continue.');
  }


  hasUpperCase(): boolean {
  return /[A-Z]/.test(this.newPassword);
}

hasLowerCase(): boolean {
  return /[a-z]/.test(this.newPassword);
}

hasNumber(): boolean {
  return /[0-9]/.test(this.newPassword);
}

hasSpecialChar(): boolean {
  return /[@$!%*?&]/.test(this.newPassword);
}
}